import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styles from './text-search.module.scss'
import { useDebounce } from '@/lib/hooks/useDebounce'
import { storeSearchParameters } from '@/store/reducers/search'
import { selectSearchTerm } from '@/store/selectors/search'
import { Size } from '@/types/system'
import { isProd } from '@/lib/utilities/system'

const Icon = dynamic(() => import('@/components/styleguide/Icon'), { ssr: false })

const SEARCH_TEXT_WRITE_DELAY = 800

type TextSearchProps = {
  isVans?: boolean
  isMobile?: boolean
  visibleFilters: string[]
}

export const TextSearch = ({
  visibleFilters = [],
  isVans = false,
  isMobile = true,
}: TextSearchProps) => {
  const dispatch = useDispatch()
  const storedTextSearch = useSelector(selectSearchTerm)
  const [localSearchTerm, setLocalSearchTerm] = useState<string>(storedTextSearch)
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)
  const debouncedSearchTerm = useDebounce<string>(localSearchTerm, SEARCH_TEXT_WRITE_DELAY)

  useEffect(() => {
    if (!isInitialLoad) {
      dispatch(
        storeSearchParameters({
          filter: 'searchTerm',
          value: debouncedSearchTerm,
          visibleFilters,
        }),
      )
    }

    if (debouncedSearchTerm !== '') {
      setIsInitialLoad(false)
    }
  }, [dispatch, debouncedSearchTerm, isInitialLoad])

  useEffect(() => {
    setLocalSearchTerm(storedTextSearch)
  }, [storedTextSearch])

  const onKeyboardEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter') {
      event.preventDefault()
    }
  }

  return (
    <>
      <span className={`${styles.label} sr-only`}>Search</span>
      <div className={styles.input}>
        <div className={styles.icon}>
          <Icon
            size={Size.Medium}
            name="Search"
          />
        </div>
        <input
          className={`${styles['search--input']}${!isProd() ? ' search__search-input' : ''}`}
          onChange={e => setLocalSearchTerm(e.target.value)}
          placeholder={
            isMobile
              ? 'Search'
              : isVans
                ? 'eg. Renault, transit or crew van'
                : 'eg. Audi, 3 series or hatchback'
          }
          type="search"
          value={localSearchTerm}
          aria-label={
            isMobile
              ? 'Search'
              : isVans
                ? 'eg. Renault, transit or crew van'
                : 'eg. Audi, 3 series or hatchback'
          }
          maxLength={50}
          onKeyDown={onKeyboardEnter}
        />
      </div>
    </>
  )
}
